import AbstractShape, { Dimensions, DimensionProperties } from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import DimensionLine from "@/Models/Lines/DimensionLine";
import GuideLine from "@/Models/Lines/GuideLine";
import { makerjs } from "../../../../source/js/shared/rendering/makerjs";
import { isEmpty } from "lodash";

export class Jewel extends AbstractShape {

	constructor( dimensions?: object ) {
		super( 'jewel', dimensions );
	}

	getHeight(): Length {
		return this.getHeightTop().add( this.getHeightBottom() );
	}

	getLength(): Length {
		return this.getWidth();
	}

	getWidth(): Length {
		return this.cleanedPrimitives.width_bottom;
	}

	getHeightTop(): Length {
		return this.cleanedPrimitives.height_top;
	}

	getHeightBottom(): Length {
		return this.cleanedPrimitives.height_bottom;
	}

	getWidthTop(): Length {
		return this.cleanedPrimitives.width_top;
	}

	getWidthBottom(): Length {
		return this.cleanedPrimitives.width_bottom;
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'width_top', secondary: 'width_bottom', tertiary: 'height_top', quaternary: 'height_bottom' };
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		let dimensionLines = [];
		if(focusedElement.includes("cutout_measurement") || forceRender) {
			dimensionLines = [
				new DimensionLine(
					[0, 0],
					[0, this.getHeight().in_mm()],
					this.renderDimensionLabel('height', this.getHeight().in_cm()),
					[-40, 0],
					ratio
				),
				new DimensionLine(
					[0, 0],
					[this.getWidth().in_mm(), 0],
					this.renderDimensionLabel('width', this.getWidth().in_cm()),
					[0, -40],
					ratio
				),
			];
		}

		if(focusedElement.includes("measurements-jewel-width_top")) {
			const offset = this.getWidth().subtract(this.getWidthTop()).divide(2).in_mm();

			dimensionLines.push(
				new DimensionLine(
					[offset, this.getHeight().in_mm()],
					[this.getWidthTop().in_mm() + offset, this.getHeight().in_mm()],
					this.renderDimensionLabel('width_top', this.getWidthTop().in_cm()),
					[0, -40],
					ratio,
					this.getDimensionLineType('measurements-jewel-width_top', focusedElement, forceRender)
				)
			);
		}
		if(focusedElement.includes("measurements-jewel-width_bottom")) {
			dimensionLines.push(
				new DimensionLine(
					[0, this.getHeightBottom().in_mm()],
					[this.getWidth().in_mm(), this.getHeightBottom().in_mm()],
					this.renderDimensionLabel('width_bottom', this.getWidthTop().in_cm()),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-jewel-width_bottom', focusedElement, forceRender)
				)
			);
		}

		if(focusedElement.includes("measurements-jewel-height_top")) {
			dimensionLines.push(
				new DimensionLine(
					[this.getWidth().divide(2).in_mm(), this.getHeightBottom().in_mm()],
					[this.getWidth().divide(2).in_mm(), this.getHeight().in_mm()],
					this.renderDimensionLabel('height_top', this.getHeightTop().in_cm()),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-jewel-height_top', focusedElement, forceRender)
				)
			);
		}

		if(focusedElement.includes("measurements-jewel-height_bottom")) {
			dimensionLines.push(
				new DimensionLine(
					[this.getWidth().divide(2).in_mm(), 0],
					[this.getWidth().divide(2).in_mm(), this.getHeightBottom().in_mm()],
					this.renderDimensionLabel('height_bottom', this.getHeightBottom().in_cm()),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-jewel-height_bottom', focusedElement, forceRender)
				)
			);
		}

		return dimensionLines;
	}

	getGuideLines(focusedElement = ''): GuideLine[] {
		if(focusedElement.includes("measurements-jewel-height_bottom") || focusedElement.includes("measurements-jewel-height_top")) {
			return [
				new GuideLine(
					[0, this.getHeightBottom().in_mm()],
					[this.getWidth().in_mm(), this.getHeightBottom().in_mm()]
				),
			];
		}

		return [];
	}

	createModel(): void {
		if(isEmpty(this.cleanedPrimitives)) {
			return;
		}

		super.createModel();
		makerjs.model.zero(this.model);
		makerjs.model.originate(this.model);
	}

	getDefaultDimensions(): Dimensions {
		return {
			width_top: 20,
			width_bottom: 40,
			height_top: 10,
			height_bottom: 40,
		};
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const height = enclosingRectangle.height();
		const width = enclosingRectangle.width();

		const height_top = height.divide(4).in_cm();
		const height_bottom = height_top + ( height_top / 2 );

		return {
			width_top: width.divide(4).in_cm(),
			width_bottom: width.divide(2).in_cm(),
			height_top: height_top,
			height_bottom: height_bottom,
		};
	}
}
